import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TestResultsWidget from './TestResultsWidget';

const TestResultsSection = ({
    data,
    isCreatingChat,
    isRegenerating,
    setCreatingChat,
    handleRegenerateRecommendation,
    getChatMessages,
    currentUser
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleChatWithEddy = async () => {
        try {
            setCreatingChat(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const chatMessages = getChatMessages(data.career.title, data.career.text);

            // Create a new chat
            const chatResponse = await fetch('/api/chat/create', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    User_ID: currentUser.User_ID,
                    initial_message: chatMessages.initialMessage
                })
            });

            if (!chatResponse.ok) {
                throw new Error('Failed to create chat');
            }

            const chatData = await chatResponse.json();

            // Generate initial response from chatbot
            const messageResponse = await fetch(`/api/chat/${chatData.Chat_ID}/message`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Text: chatMessages.responsePrompt,
                    reference_analysis_id: null
                })
            });

            if (!messageResponse.ok) {
                throw new Error('Failed to get chatbot response');
            }

            navigate('/eddy', { state: { chatId: chatData.Chat_ID } });
        } catch (error) {
            console.error('Error creating chat:', error);
            alert('Failed to start chat. Please try again.');
        } finally {
            setCreatingChat(false);
        }
    };

    return (
        <div className="p-6 rounded-lg bg-surface border border-edge shadow-sm mb-6">
            <div className="flex flex-col lg:flex-row gap-6">
                <div className="w-full lg:w-1/2">
                    <h3 className="text-xl font-bold text-ink mb-6">{t('report.yourAbilityScores')}</h3>
                    <TestResultsWidget
                        abilityScores={data.abilities}
                        showHeading={false}
                        className="mb-0"
                    />
                </div>

                <div className="w-full lg:w-1/2">
                    <h3 className="text-xl font-bold text-ink mb-6">{t('report.eddysRecommendation')}</h3>
                    <div className="bg-surface-muted border border-edge rounded-lg shadow-sm p-6">
                        <h4 className="font-semibold text-ink mb-2">{data.career.title}</h4>
                        <p className="text-ink-muted mb-4">{data.career.text}</p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleChatWithEddy}
                                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface disabled:opacity-50"
                                disabled={isCreatingChat}
                            >
                                {isCreatingChat ? t('report.creatingChat') : t('report.chatWithEddy')}
                            </button>
                            <button
                                onClick={handleRegenerateRecommendation}
                                className="flex-1 bg-success text-primary-foreground px-4 py-2 rounded-full hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 focus:ring-offset-surface disabled:opacity-50"
                                disabled={isRegenerating}
                            >
                                {isRegenerating ? t('report.regeneratingRecommendation') : t('report.regenerateRecommendation')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recommendations Section */}
            <div className="mt-12">
                <h3 className="text-xl font-bold text-ink mb-6">{t('report.recommendedSubjects')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {data.recommendations.subjects.map((subject, index) => (
                        <div key={index} className="bg-surface-muted border border-edge rounded-lg shadow-sm p-6">
                            <h4 className="font-semibold text-ink">{subject.name}</h4>
                            <div className="mt-2">
                                <div className="w-full bg-edge rounded-full h-2.5">
                                    <div
                                        className="bg-success h-2.5 rounded-full"
                                        style={{ width: `${subject.score}%` }}
                                    ></div>
                                </div>
                                <p className="text-sm text-ink-subtle mt-1">{Math.round(subject.score)}% {t('report.match')}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-12">
                <h3 className="text-xl font-bold text-ink mb-6">{t('report.recommendedProgrammes')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {data.recommendations.programs.map((program, index) => (
                        <div key={index} className="bg-surface-muted border border-edge rounded-lg shadow-sm p-6">
                            <h4 className="font-semibold text-ink">{program.name}</h4>
                            <p className="text-sm text-ink-muted">{program.university}</p>
                            <p className="text-sm text-ink-subtle">{program.faculty}</p>
                            <div className="mt-2">
                                <div className="w-full bg-edge rounded-full h-2.5">
                                    <div
                                        className="bg-link h-2.5 rounded-full"
                                        style={{ width: `${program.score}%` }}
                                    ></div>
                                </div>
                                <p className="text-sm text-ink-subtle mt-1">{Math.round(program.score)}% {t('report.match')}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TestResultsSection; 